import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Bot, Info, Settings } from "lucide-react";
import * as React from "react";
import { Link, matchPath, Outlet, useLocation, useParams } from "react-router";

import { useAuth } from "@/providers";
import {
  PageBreadcrumbs,
  PageLayout,
  RobotoDivider,
  RobotoStyledTab,
  RobotoStyledTabs,
  TagList,
} from "@/shared/components";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import type { DeviceTypeRecord } from "@/shared/domain/deviceTypes";
import { useDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { Callout, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import styles from "./DeviceType.module.css";
import { DeviceTypeTab } from "./deviceTypeTab";
import { EditableDeviceTypeDescription } from "./EditableDeviceTypeDescription";
import { EditableDeviceTypeName } from "./EditableDeviceTypeName";
import type { DeviceTypeContext } from "./useDeviceTypeContext";

const TABS = [
  {
    value: DeviceTypeTab.Devices,
    label: "Devices",
    Icon: Bot,
  },
  {
    value: DeviceTypeTab.Configurations,
    label: "Configurations",
    Icon: Settings,
  },
  { value: DeviceTypeTab.Details, label: "Details", Icon: Info },
];

/**
 * The tab named by the first segment after the type's ID, or Devices when that
 * segment is missing or names no tab. Later segments are ignored: under
 * Configurations they are the path of the open folder.
 */
function useActiveTab(): DeviceTypeTab {
  const { pathname } = useLocation();
  const tab = matchPath(
    { path: `${Goto.route.DeviceTypes}/:deviceTypeId/:tab`, end: false },
    pathname,
  )?.params.tab;
  return (
    TABS.find((candidate) => candidate.value === tab)?.value ??
    DeviceTypeTab.Devices
  );
}

/**
 * Layout of `/device-types/:deviceTypeId`: the breadcrumbs, the header block
 * (name, description, tags, facts and the tab strip) and the active tab. The
 * tabs mount only once the type has loaded, and read it through
 * `useDeviceTypeContext`; the header and tab strip render on every branch.
 */
export function DeviceType() {
  const { deviceTypeId } = useParams();
  const { currentOrganization } = useAuth();
  const deviceTypeQuery = useDeviceType(
    deviceTypeId,
    currentOrganization?.org_id,
  );
  const deviceType = deviceTypeQuery.data ?? null;

  // Publish the device type to AI Chat so the agent can resolve "this device
  // type" without the user pasting its id. The id holds while the record loads
  // or fails; the record adds the name. No `orgId`, as for devices (see
  // `ViewingContextMap`).
  useRegisterViewingContext("deviceType", {
    deviceTypeId,
    record: deviceType ?? undefined,
  });

  const outletContext = React.useMemo<DeviceTypeContext | null>(
    () => (deviceType ? { deviceType } : null),
    [deviceType],
  );
  const name = deviceType?.name ?? deviceTypeId ?? "";

  let body: React.ReactNode;
  if (deviceTypeQuery.error) {
    body = <Callout variant="error">{deviceTypeQuery.error.message}</Callout>;
  } else if (!outletContext) {
    body = (
      <p className={styles.loading}>
        <Spinner aria-hidden />
        Loading device type&hellip;
      </p>
    );
  } else {
    body = <Outlet context={outletContext} />;
  }

  return (
    <PageLayout
      title={`Roboto - Device Type - ${name}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Devices}>Devices</Link>
          <Link to={Goto.route.DeviceTypes}>Device Types</Link>
          <span>{name}</span>
        </PageBreadcrumbs>
      }
    >
      <div className={styles.root}>
        <DeviceTypeChrome name={name} deviceType={deviceType} />
        <div className={styles.tabPanel}>{body}</div>
      </div>
    </PageLayout>
  );
}

/**
 * Title row, description, tags, metadata strip and tab bar. With no record
 * (loading or failed) the heading falls back to `name` and the rest of the
 * facts are omitted; the tab strip reads its active value from the URL and
 * renders regardless.
 */
function DeviceTypeChrome({
  name,
  deviceType,
}: {
  name: string;
  deviceType: DeviceTypeRecord | null;
}) {
  return (
    <div className={styles.headerSection}>
      <div className={styles.titleRow}>
        {deviceType ? (
          <EditableDeviceTypeName
            key={deviceType.device_type_id}
            deviceType={deviceType}
          />
        ) : (
          <h1 className={styles.title}>{name}</h1>
        )}
      </div>

      {deviceType && (
        <div className={styles.descriptionRow}>
          <EditableDeviceTypeDescription
            key={deviceType.device_type_id}
            deviceType={deviceType}
          />
        </div>
      )}

      {deviceType && deviceType.tags.length > 0 && (
        <div className={styles.tagsRow}>
          <TagList tags={deviceType.tags} maxDisplayCount={5} />
        </div>
      )}

      {deviceType && <DeviceTypeMetadataRow deviceType={deviceType} />}

      <DeviceTypeTabBar />
    </div>
  );
}

function DeviceTypeMetadataRow({
  deviceType,
}: {
  deviceType: DeviceTypeRecord;
}) {
  return (
    <div className={styles.metadataRow}>
      <span className={styles.metadataField}>
        <CalendarTodayOutlinedIcon className={styles.metadataIcon} />
        <span className={styles.metadataLabel}>Created:</span>
        <span className={styles.metadataText}>
          <FormattedDateTime value={deviceType.created} time="none" />
        </span>
      </span>

      <span className={styles.metadataField}>
        <span className={styles.metadataLabel}>Created By:</span>
        <RobotoPrincipalUnderlineLink principal={deviceType.created_by} icon />
      </span>

      <span className={styles.metadataField}>
        <Bot size="1em" className={styles.metadataIcon} />
        <Link
          className={styles.metadataLink}
          to={Goto.createDeviceTypeRoute(
            deviceType.device_type_id,
            DeviceTypeTab.Devices,
          )}
        >
          {deviceType.device_count === 1
            ? "1 device"
            : `${deviceType.device_count} devices`}
        </Link>
      </span>
    </div>
  );
}

function DeviceTypeTabBar() {
  const activeTab = useActiveTab();
  return (
    <div className={styles.tabBar}>
      <RobotoStyledTabs value={activeTab} aria-label="Device type">
        {TABS.map(({ value, label, Icon }) => (
          <RobotoStyledTab
            key={value}
            value={value}
            label={label}
            icon={<Icon size="1em" className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={value}
            replace
          />
        ))}
      </RobotoStyledTabs>
      <RobotoDivider />
    </div>
  );
}
